<template>
  <l-map
    :center="mapOptions.center"
    :zoom.sync="mapOptions.zoom"
    :minZoom="mapOptions.minZoom"
    :maxZoom="mapOptions.maxZoom"
    :maxBounds="mapOptions.maxBounds"
    :scrollWheelZoom="mapOptions.scrollWheelZoom"
    :options="{attributionControl: mapOptions.attributionControl}"
    :crs="mapOptions.crs"
    style="height: 100%"
    ref="map"
    @ready="$emit('ready')"
    @update:zoom="updateZoom($event)"
    @update:center="updateCenter($event)"
    aria-label="Map"
    role="application"
  >
    <template v-if="tilesReady">
      <map-layer-group v-for="layer in mapLayers"
                      :key="layer.id"
                      :name="layer.name"
                      :visible="layer.id === visibleLayer"
                      >
        <l-tile-layer v-for="image in layer.images"
                      :key="image.id"
                      :url="`/file/tile/${image.id}`"
                      :tileLayerClass="zoomify"
                      :options="{
                        width: image.width,
                        height: image.height,
                        tileSize: 256
                      }"
                      :visible="image.id === visibleImage"
                      />
        <l-layer-group v-for="category in layer.categories" :key="category.id" :visible="category.checked">
          <l-marker :lat-lng="[pin.posX, pin.posY]"
                    :key="pin.id"
                    :draggable="settings.markersDraggable || false"
                    :options="{ alt: pin.name }"
                    v-for="pin in category.pins"
                    @ready="onReadyPin"
                    @click="$emit('select-pin', pin)"
                    @update:latLng="$emit('update-pin-position', [pin, $event.lat, $event.lng])">
            <l-tooltip>
                {{ pin.name }}
            </l-tooltip>
            <map-icon :pin="pin"
                      :category="category"
                      :styleColors="settings.colors"
                      :mediaRepresentation="settings.mediaRepresentation"
                      :bookmarkColor="settings.bookmarkColor"
                      :bookmarkOutlineColor="settings.bookmarkOutlineColor"
                      :isBookmark="isBookmark(pin)" />
          </l-marker>
        </l-layer-group>
      </map-layer-group>
    </template>
    <l-control v-if="settings.mapDownload && settings.mapDownload.path" position="topleft">
      <a :href="settings.mapDownload.path" download class="map-button btn-download" title="Download map"><i class="fa fa-download"></i></a>
    </l-control>
    <layer-control :layers="mapLayers"
                   :visible-layer="visibleLayer"
                   :visible-image="visibleImage"
                   :styleColors="settings.colors"
                   :layersLabel="settings.layersLabel"
                   :imagesLabel="settings.imagesLabel"
                   @select-layer="selectLayer($event)"
                   @select-image="selectImage($event)">
    </layer-control>
    <l-control v-if="returnUrl" position="topleft">
      <a :href="returnUrl" class="map-button btn-back" title="Go back">
        <fa icon="fa-solid fa-arrow-left" />
      </a>
    </l-control>
    <slot></slot>
    <template v-if="settings.mapArrows">
      <button type="button" @click="moveMap('left')" class="map-show-more show-more-left-button">
        <fa icon="fa-solid fa-chevron-left" />
      </button>
      <button type="button" @click="moveMap('right')" class="map-show-more show-more-right-button">
        <fa icon="fa-solid fa-chevron-right" />
      </button>
    </template>
  </l-map>
</template>

<script>
import 'leaflet/dist/leaflet.css'
import { CRS, latLng, DomEvent } from 'leaflet'
import { LMap, LTileLayer, LMarker, LTooltip, LControl, LLayerGroup } from 'vue2-leaflet'
import { initTiles, initZoomify } from './zoomify'
import MapLayerGroup from './MapLayerGroup'
import MapIcon from './MapIcon'
import LayerControl from './LayerControl'

export default {
  name: 'Map',
  components: {
    LControl,
    MapLayerGroup,
    MapIcon,
    LayerControl,
    LLayerGroup,
    LMap,
    LMarker,
    LTileLayer,
    LTooltip
  },
  props: {
    mapLayers: {
      type: Array,
      default: () => []
    },
    settings: {
      type: Object,
      default: () => {}
    },
    selectedLayer: {
      type: Number,
      default: null
    },
    selectedImage: {
      type: Number,
      default: null
    },
    selectedPin: {
      type: Object,
      default: null
    },
    bookmarks: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      tilesReady: false,
      currentCenter: this.settings.center || latLng(0, 0),
      currentZoom: this.settings.zoomInit || 0,
      previousCenter: null,
      previousZoom: null,
      mapOptions: {
        center: this.settings.center || latLng(0, 0),
        zoom: this.settings.zoomInit || 0,
        minZoom: this.settings.zoomMin || null,
        maxZoom: this.settings.zoomMax || null,
        maxBounds: null,
        scrollWheelZoom: true,
        attributionControl: false,
        crs: CRS.Simple
      },
      visibleLayer: null,
      visibleImage: null
    }
  },
  created () {
    this.updateLayers()
  },
  mounted () {
    this.$nextTick(() => {
      this.mapOptions.maxBounds = this.getBounds()

      // Emit selected layer event
      // this.$refs.map.mapObject.on('baselayerchange', e => {
      //   for (const layer of this.mapLayers) {
      //     if (layer.name === e.name && this.visibleLayer !== layer.id) {
      //       this.visibleLayer = layer.id
      //       this.visibleImage = layer.images[0].id

      //       this.$emit('select-layer', layer)
      //       this.$emit('select-image', layer.images[0])

      //       break
      //     }
      //   }
      // })
    })
  },
  computed: {
    returnUrl () {
      let params = new URLSearchParams(window.location.search)

      if (params.get('return_url')) {
        return params.get('return_url')
      } else {
        return null
      }
    } 
  },
  methods: {
    onReadyPin ($marker) {
      $marker._icon.setAttribute('role', 'button');
      // Supposed to trigger marker tooltips on tabbing through them
      // Commented this out as it only works for the first layer and produces a lot of console errors
      //$marker._icon.focusin = () => $marker._icon.dispatchEvent(new MouseEvent('mouseover', { 'bubbles': true }))
      //$marker._icon.focusout = () => $marker._icon.dispatchEvent(new MouseEvent('mouseout', { 'bubbles': true }))

      // A dirty hack to select the pin (and open its popup) by pressing the Enter key on it
      // Apparently default Vue mechanism doesn't work for Vue2-leaflet's LMarker somehow
      // There might be a proper fix but it wasn't investigated down to the root
      DomEvent.on($marker, 'keypress', (e) => {
        if (e.originalEvent.key === 'Enter') {
          $marker._icon.dispatchEvent(new MouseEvent('click', { 'bubbles': true }))
        }
      }) 
    },
    zoomify: initZoomify,
    updateLayers () {
      if (this.mapLayers.length > 0) {
        this.loadTiles()

        // Set first map layer to be visible
        this.visibleLayer = this.mapLayers[0].id
        this.visibleImage = this.mapLayers[0].images[0].id

        this.$emit('select-layer', this.mapLayers[0])
        this.$emit('select-image', this.mapLayers[0].images[0])
      }
    },
    loadTiles () {
      this.tilesReady = false

      let tileInits = []

      for (const layer of this.mapLayers) {
        for (const image of layer.images) {
          tileInits.push(initTiles(image.id, this.settings.baseURL))
        }
      }

      Promise.all(tileInits).then(() => {
        this.tilesReady = true
        this.$emit('tiles-loaded')
      })
    },
    getBounds () {
      if (this.mapLayers.length === 0) {
        return
      }

      let map = this.$refs.map.mapObject
      let zoom, width, height

      for (const layer of this.mapLayers) {
        for (const image of layer.images) {
          let layerZoom = Math.min(
            Math.ceil(Math.log2(image.width / 3) - 7),
            Math.ceil(Math.log2(image.height / 3) - 7)
          )

          if (layerZoom < zoom || zoom === undefined) {
            zoom = layerZoom - 1
            width = image.width
            height = image.height
          }
        }
      }

      let southWest = map.unproject([0, height], zoom)
      let northEast = map.unproject([width, 0], zoom)

      return [southWest, northEast]
    },
    updateCenter (center) {
      this.currentCenter = center

      this.$emit('update-center', center)
    },
    updateZoom (zoom) {
      this.currentZoom = zoom

      this.$emit('update-zoom', zoom)
    },
    moveMap (moveDirection) {
      const map = this.$refs.map.mapObject
      const center = map.getCenter()
      const stepMove = this.mapOptions.maxBounds[1].lng * (10 / 100)

      switch (moveDirection) {
          case 'left':
              map.flyTo([center.lat, center.lng - stepMove])
          break;
          case 'right':
              map.flyTo([center.lat, center.lng + stepMove])
          break;
      }
    },
    selectLayer (layer) {
      this.visibleLayer = layer.id
      this.visibleImage = layer.images[0].id
      this.$emit('select-layer', layer)
      this.$emit('select-image', layer.images[0])
    },
    selectImage (image) {
      this.visibleImage = image.id
      this.$emit('select-image', image)
    },
    isBookmark (pin) { 
      return this.bookmarks.some(bookmark => bookmark.title === pin.name)
    }
  },
  watch: {
    mapLayers: function (newLayers) {
      if (newLayers.length > 0) {
        this.updateLayers()
        this.mapOptions.maxBounds = this.getBounds()
      }
    },
    settings: {
      handler: function (newSettings) {
        if (newSettings.center) {
          this.mapOptions.center = latLng(newSettings.center)
        }
        if (newSettings.zoomInit !== undefined && newSettings.zoomInit !== null) {
          this.mapOptions.zoom = newSettings.zoomInit
        }
      },
      immediate: true
    },
    selectedLayer: function (newLayer) {
      if (newLayer) {
        this.visibleLayer = newLayer
        this.visibleImage = this.mapLayers.find(layer => layer.id === newLayer).images[0].id
      } else {
        this.visibleLayer = this.mapLayers[0].id
        this.visibleImage = this.mapLayers[0].images[0].id
      }
    },
    selectedImage: function (newImage) {
      if (newImage) {
        this.visibleImage = newImage
      } else {
        this.visibleImage = this.mapLayers[0].images[0].id
      }
    },
    selectedPin: function (newPin) {
      const map = this.$refs.map.mapObject

      if (newPin) {
        this.previousCenter = this.currentCenter
        this.previousZoom = this.currentZoom

        map.flyTo([newPin.posX, newPin.posY], this.settings.selectZoomDisabled ? this.previousZoom : map.getMaxZoom())
      } else {
        map.flyTo(this.previousCenter, this.previousZoom)
      }
    }
  }
}
</script>

<style lang="scss">
@keyframes bounce-left {
  from {margin-left: 0px;}
  to {margin-left: 10px;}
}
@keyframes bounce-right {
  from {margin-right: 0px;}
  to {margin-right: 10px;}
}
.map-button {
  border: 2px solid rgba(0,0,0,0.2);
  background-clip: padding-box;
  box-shadow: none;
  border-radius: 4px;
  width: 30px;
  height: 30px;
  line-height: 30px;
  font-size: 16px;
  background-color: #fff;
  display: block;
  box-sizing: content-box;
  text-align: center;
  i {
    color: #fff;
  }
  &.btn-download {
    i {
      color: black;
    }
  }
  &.focus-visible:focus {
    outline: 2px solid var(--quaternary) !important;
  }
  &.btn-back {
    width: 24px;
    height: 24px;
    line-height: 24px;
    font-size: 1em;
    background-color: var(--primary);
    border-width: 1px;
    margin-top: 10px;

  }
}
.vue2leaflet-map.focus-visible:focus::after {
  width: 100%;
  height: 100%;
  content: '';
  outline: 2px solid var(--quaternary) !important;
  outline-offset: -2px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1000;
}

.leaflet-control-zoom-in,
.leaflet-control-zoom-out,
.leaflet-control-layers-toggle,
.leaflet-marker-icon,
.map-button {
  &.focus-visible:focus {
    outline: 2px solid var(--quaternary) !important;
  }
}
.map-show-more {
  display: flex;
  position: absolute;
  background: var(--primary);
  padding: 5px 10px;
  border-radius: 100%;
  border: solid 5px var(--secondary);
  top: 50%;
  transform: translateY(-50%);
  margin-top: -25px;
  z-index: 1000;
  outline: none;
  font-size: 30px;
  color: var(--secondary);
  &:hover, &:active {
    outline: 0;
    border-radius: 100%;
    border: 5px solid var(--primary);
    background: var(--secondary);
    color: var(--primary);
  }
}
.show-more-left-button{
  left: 50px;
  animation: bounce-left 500ms linear infinite alternate;
  i{
    right: 2px;
  }
}
.show-more-right-button{
  right: 50px;
  animation: bounce-right 500ms linear infinite alternate;
  i{
    right: -2px;
  }
}
</style>
